import React, { useState } from 'react';
import { brandColors } from '../styles/clientManagementStyles';

/**
 * "+ Add a day" at the foot of the client program editor (owner, 23 October 2026). A named, empty day at a
 * chosen place, from the client's next week (or the later week being viewed) and in every week after it;
 * the week the client is in never changes (migration 087). `days` are the viewed week's days, for the
 * places; `fromWeek` is the week it starts from. The place is the start, the end, or after a day named by its
 * identity (slot_key), so it means the same in every week changed even where that week's days differ from the
 * one on screen (Codex's review 032, R1). onAdd({ name, place, afterSlot, placeLabel }) resolves true once added.
 */
const dayLabel = (d) => (d ? `Day ${d.day_number}${d.name ? ` (${d.name})` : ''}` : 'that day');

const AddDayPanel = ({ days = [], fromWeek, onAdd, saving = false }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [place, setPlace] = useState('end');
  const trimmed = name.trim();

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setPlace('end'); }}
        style={{
          width: '100%', padding: '12px', minHeight: 44, borderRadius: '8px', cursor: 'pointer',
          border: `1px dashed ${brandColors.gold}`, backgroundColor: 'transparent', color: brandColors.gold,
          fontSize: '13px', fontWeight: '600',
        }}
      >
        + Add a day
      </button>
    );
  }

  const add = async () => {
    if (!trimmed || saving) return;
    const after = place.startsWith('after:') ? place.slice(6) : null;
    const placeLabel = after ? `after ${dayLabel(days.find(d => d.slot_key === after))}` : place === 'start' ? 'at the start' : 'at the end';
    const ok = await onAdd({ name: trimmed, place: after ? 'after' : place, afterSlot: after, placeLabel });
    if (ok) { setOpen(false); setName(''); }
  };

  return (
    <div role="group" aria-label="Add a day" style={{ backgroundColor: brandColors.darkGray, borderRadius: '8px', padding: '16px', border: `1px solid ${brandColors.lightGray}` }}>
      <div style={{ color: brandColors.white, fontSize: '13px', fontWeight: '600', marginBottom: '4px' }}>Add a day</div>
      <p style={{ color: brandColors.silver, fontSize: '12px', margin: '0 0 12px' }}>
        From week {fromWeek} and every week after it. The week the client is in stays as it is. The day starts
        empty: add its exercises with + Add Exercise.
      </p>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          aria-label="Name of the day"
          placeholder="e.g. Cardio"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          style={{ flex: '1 1 180px', padding: '10px', minHeight: 44, borderRadius: '8px', border: `1px solid ${brandColors.lightGray}`, backgroundColor: 'transparent', color: brandColors.white, fontSize: '13px' }}
        />
        <select
          aria-label="Where in the week"
          value={place}
          onChange={(e) => setPlace(e.target.value)}
          style={{ flex: '1 1 180px', padding: '10px', minHeight: 44, borderRadius: '8px', border: `1px solid ${brandColors.lightGray}`, backgroundColor: brandColors.darkGray, color: brandColors.white, fontSize: '13px' }}
        >
          <option value="start">At the start</option>
          {days.filter((d, i) => d.slot_key && i < days.length - 1 && days[i + 1].day_number !== d.day_number).map((d) => (
            <option key={d.id} value={`after:${d.slot_key}`}>After {dayLabel(d)}</option>
          ))}
          <option value="end">At the end</option>
        </select>
        <button
          onClick={add}
          disabled={!trimmed || saving}
          style={{ padding: '10px 18px', minHeight: 44, borderRadius: '8px', border: 'none', cursor: trimmed && !saving ? 'pointer' : 'default', backgroundColor: brandColors.gold, color: '#000', fontWeight: '700', fontSize: '13px', opacity: trimmed && !saving ? 1 : 0.5 }}
        >
          {saving ? 'Adding…' : 'Add day'}
        </button>
        <button
          onClick={() => setOpen(false)}
          disabled={saving}
          style={{ padding: '10px 14px', minHeight: 44, borderRadius: '8px', border: `1px solid ${brandColors.lightGray}`, backgroundColor: 'transparent', color: brandColors.silver, fontSize: '13px', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default AddDayPanel;
